<template>
  <div class="root-container">
    <div class="table-container">
      <!-- 搜索栏 -->
      <div class="search-container" :class="{ 'mobile-search-top': !isDesktop }">
        <!-- 电脑端 -->
        <div v-if="isDesktop" class="desktop-search-wrapper">
          <div class="search-row">
            <el-input
              v-model="listQuery.keyword"
              placeholder="搜索项目名称"
              prefix-icon="el-icon-search"
              class="search-input"
              @keyup.enter="handleFilter"
            >
              <template slot="append">
                <el-button @click="handleFilter">搜索</el-button>
              </template>
            </el-input>

            <div class="button-group">
              <el-button type="primary" size="small" icon="el-icon-plus" @click="handleCreate" class="add-button">
                新建项目
              </el-button>
              <el-button type="primary" size="small" icon="el-icon-filter" @click="toggleFilter" class="filter-button">
                {{ isFilterOpen ? '收起筛选' : '展开筛选' }}
              </el-button>
            </div>
          </div>

          <!-- 电脑端筛选面板 -->
          <div v-if="isFilterOpen" class="filter-panel">
            <div class="filter-row">
              <div class="filter-item">
                <label>状态：</label>
                <el-select v-model="listQuery.status" placeholder="项目状态" clearable style="width: 150px;">
                  <el-option label="进行中" :value="0" />
                  <el-option label="已完成" :value="1" />
                  <el-option label="已暂停" :value="2" />
                  <el-option label="已归档" :value="3" />
                </el-select>
              </div>
              <div class="filter-item">
                <label>优先级：</label>
                <el-select v-model="listQuery.priority" placeholder="优先级" clearable style="width: 150px;">
                  <el-option label="低" :value="0" />
                  <el-option label="中" :value="1" />
                  <el-option label="高" :value="2" />
                  <el-option label="紧急" :value="3" />
                </el-select>
              </div>
            </div>
            <div class="filter-actions">
              <div class="applied-filters">
                <span v-if="listQuery.status === undefined && listQuery.priority === undefined" class="filter-tag filter-tag-empty">
                  当前无筛选条件
                </span>
                <span v-if="listQuery.status !== undefined && listQuery.status !== null && listQuery.status !== ''" class="filter-tag">
                  状态：{{ statusText(listQuery.status) }}
                  <i class="el-icon-close" style="margin-left:5px;cursor:pointer;" @click="listQuery.status = undefined"></i>
                </span>
                <span v-if="listQuery.priority !== undefined && listQuery.priority !== null && listQuery.priority !== ''" class="filter-tag">
                  优先级：{{ priorityText(listQuery.priority) }}
                  <i class="el-icon-close" style="margin-left:5px;cursor:pointer;" @click="listQuery.priority = undefined"></i>
                </span>
              </div>
              <div class="filter-buttons">
                <el-button size="small" @click="resetFilter">重置筛选</el-button>
                <el-button type="primary" size="small" @click="applyFilter">应用筛选</el-button>
              </div>
            </div>
          </div>
        </div>

        <!-- 手机端 -->
        <div v-else class="mobile-search-wrapper">
          <van-search
            v-model="listQuery.keyword"
            placeholder="搜索项目名称"
            show-action
            @search="handleFilter"
          >
            <template #action>
              <div @click="handleFilter">搜索</div>
            </template>
          </van-search>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="content-container">
        <!-- 电脑端表格 -->
        <div class="table-wrapper" v-if="isDesktop">
          <el-table
            :data="list"
            v-loading="listLoading"
            style="width: 100%"
            tooltip-effect="dark"
            empty-text="暂无数据"
            class="desktop-table"
            :height="tableHeight"
          >
            <el-table-column label="项目名称" min-width="200">
              <template slot-scope="{row}">
                <span class="project-name link-type" @click="handleViewBoard(row)">{{ row.name }}</span>
                <div v-if="row.description" class="project-desc">{{ row.description }}</div>
              </template>
            </el-table-column>
            <el-table-column label="负责人" width="120" align="center">
              <template slot-scope="{row}">
                <span>{{ row.owner ? row.owner.nickname : '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="进度" width="160" align="center">
              <template slot-scope="{row}">
                <el-progress :percentage="row.progress || 0" :status="row.progress === 100 ? 'success' : ''" />
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="{row}">
                <el-tag :type="statusType(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="优先级" width="90" align="center">
              <template slot-scope="{row}">
                <el-tag :type="priorityType(row.priority)" size="small">{{ priorityText(row.priority) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="开始日期" width="115" align="center">
              <template slot-scope="{row}">
                <span>{{ row.start_date || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="截止日期" width="115" align="center">
              <template slot-scope="{row}">
                <span>{{ row.end_date || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="300" align="center" fixed="right" class-name="action-column">
              <template slot-scope="{row}">
                <div style="white-space:nowrap;">
                  <el-button type="primary" size="mini" icon="el-icon-view" @click="handleViewBoard(row)">进入看板</el-button>
                  <el-button type="primary" size="mini" icon="el-icon-edit" @click="handleUpdate(row)">编辑</el-button>
                  <el-button type="danger" size="mini" icon="el-icon-delete" @click="handleDelete(row)">删除</el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 手机端卡片 -->
        <div class="mobile-list" v-else>
          <van-list v-model="listLoading" :finished="mobileFinished" finished-text="没有更多了" @load="onMobileLoad">
            <div v-for="item in list" :key="item.id" class="mobile-card" @click="handleViewBoard(item)">
              <div class="mobile-card-header">
                <span class="mobile-title">{{ item.name }}</span>
                <el-tag :type="statusType(item.status)" size="mini">{{ statusText(item.status) }}</el-tag>
              </div>
              <div class="mobile-card-body">
                <span class="mobile-meta">负责人：{{ item.owner ? item.owner.nickname : '-' }}</span>
                <el-tag :type="priorityType(item.priority)" size="mini" style="margin-left:8px;">{{ priorityText(item.priority) }}</el-tag>
              </div>
              <el-progress :percentage="item.progress || 0" style="margin-top:8px;" />
              <div class="mobile-card-footer">
                <span v-if="item.end_date" class="mobile-date">截止：{{ item.end_date }}</span>
                <div class="mobile-actions" @click.stop>
                  <van-button type="primary" size="mini" @click="handleUpdate(item)">编辑</van-button>
                  <van-button type="danger" size="mini" @click="handleDelete(item)" style="margin-left:6px;">删除</van-button>
                </div>
              </div>
            </div>
          </van-list>
        </div>
      </div>

      <!-- 分页 -->
      <div v-if="isDesktop && total > 0" class="pagination-container">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          :page-size.sync="listQuery.pageSize"
          :current-page.sync="listQuery.page"
          :page-sizes="[10, 20, 50, 100]"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </div>

    <!-- 项目表单弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="680px" :close-on-click-modal="false">
      <el-form
        ref="dataForm"
        :model="form"
        :rules="rules"
        label-position="right"
        label-width="90px"
      >
        <el-form-item label="项目名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入项目名称" />
        </el-form-item>
        <el-form-item label="项目描述">
          <el-input v-model="form.description" type="textarea" :rows="3" placeholder="请输入项目描述" />
        </el-form-item>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="负责人" prop="owner_id">
              <el-select v-model="form.owner_id" placeholder="请选择负责人" style="width: 100%;">
                <el-option
                  v-for="user in userList"
                  :key="user.id"
                  :label="user.nickname"
                  :value="user.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="优先级">
              <el-select v-model="form.priority" style="width: 100%;">
                <el-option label="低" :value="0" />
                <el-option label="中" :value="1" />
                <el-option label="高" :value="2" />
                <el-option label="紧急" :value="3" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="开始日期">
              <el-date-picker v-model="form.start_date" type="date" placeholder="选择开始日期" value-format="yyyy-MM-dd" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="截止日期">
              <el-date-picker v-model="form.end_date" type="date" placeholder="选择截止日期" value-format="yyyy-MM-dd" style="width: 100%;" />
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="项目状态">
          <el-radio-group v-model="form.status">
            <el-radio :label="0">进行中</el-radio>
            <el-radio :label="1">已完成</el-radio>
            <el-radio :label="2">已暂停</el-radio>
            <el-radio :label="3">已归档</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="项目成员">
          <el-select v-model="form.members" multiple placeholder="请选择项目成员" style="width: 100%;">
            <el-option
              v-for="user in userList"
              :key="user.id"
              :label="user.nickname"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitForm">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getProjects, createProject, updateProject, deleteProject } from '@/api/task'
import { getAdmins } from '@/api/system'

export default {
  name: 'TaskProjects',
  data() {
    return {
      list: [],
      total: 0,
      listLoading: false,
      isDesktop: true,
      isFilterOpen: false,
      mobileFinished: false,
      listQuery: {
        page: 1,
        pageSize: 10,
        keyword: '',
        status: undefined,
        priority: undefined
      },
      userList: [],
      dialogVisible: false,
      dialogTitle: '',
      submitLoading: false,
      form: {
        id: undefined,
        name: '',
        description: '',
        owner_id: undefined,
        priority: 1,
        status: 0,
        start_date: '',
        end_date: '',
        members: []
      },
      rules: {
        name: [{ required: true, message: '请输入项目名称', trigger: 'blur' }],
        owner_id: [{ required: true, message: '请选择负责人', trigger: 'change' }]
      }
    }
  },
  computed: {
    tableHeight() {
      if (!this.isDesktop) return 'auto'
      var headerHeight = 60
      var searchHeight = this.isFilterOpen ? 180 : 60
      var paginationHeight = 60
      var footerHeight = 40
      var padding = 20
      return window.innerHeight - headerHeight - searchHeight - paginationHeight - footerHeight - padding - 100
    }
  },
  mounted() {
    this.checkScreenSize()
    window.addEventListener('resize', this.handleResize)
    this.getList()
    this.getUserList()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    handleResize() {
      this.checkScreenSize()
    },
    checkScreenSize() {
      this.isDesktop = window.innerWidth > 768
    },
    toggleFilter() {
      this.isFilterOpen = !this.isFilterOpen
    },
    handleFilter() {
      this.listQuery.page = 1
      this.getList()
    },
    applyFilter() {
      this.listQuery.page = 1
      this.getList()
    },
    resetFilter() {
      this.listQuery.status = undefined
      this.listQuery.priority = undefined
      this.listQuery.page = 1
      this.getList()
    },
    onMobileLoad() {
      if (this.list.length >= this.total) {
        this.mobileFinished = true
        return
      }
      this.listQuery.page++
      this.getList(true)
    },
    getList(append) {
      this.listLoading = true
      var params = {
        page: this.listQuery.page,
        page_count: this.listQuery.pageSize
      }
      if (this.listQuery.keyword) params.keyword = this.listQuery.keyword
      if (this.listQuery.status !== undefined && this.listQuery.status !== null && this.listQuery.status !== '') params.status = this.listQuery.status
      if (this.listQuery.priority !== undefined && this.listQuery.priority !== null && this.listQuery.priority !== '') params.priority = this.listQuery.priority

      getProjects(params).then(function(res) {
        if (res.code === 200) {
          var data = res.data || {}
          var newList = data.list || data.data || []
          if (append) {
            this.list = this.list.concat(newList)
          } else {
            this.list = newList
          }
          this.total = data.total || 0
          if (this.list.length >= this.total) this.mobileFinished = true
        }
        this.listLoading = false
      }.bind(this)).catch(function() {
        this.listLoading = false
      }.bind(this))
    },
    getUserList() {
      getAdmins().then(function(res) {
        if (res.code === 200) {
          this.userList = Array.isArray(res.data) ? res.data : []
        }
      }.bind(this))
    },
    handleSizeChange(val) {
      this.listQuery.pageSize = val
      this.listQuery.page = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.getList()
    },
    handleCreate() {
      this.dialogTitle = '新建项目'
      this.form = { id: undefined, name: '', description: '', owner_id: undefined, priority: 1, status: 0, start_date: '', end_date: '', members: [] }
      this.dialogVisible = true
      this.$nextTick(function() {
        if (this.$refs.dataForm) this.$refs.dataForm.clearValidate()
      }.bind(this))
    },
    handleUpdate(row) {
      this.dialogTitle = '编辑项目'
      this.form = {
        id: row.id,
        name: row.name,
        description: row.description || '',
        owner_id: row.owner_id,
        priority: row.priority,
        status: row.status,
        start_date: row.start_date || '',
        end_date: row.end_date || '',
        members: row.members ? row.members.map(function(m) { return m.user_id }) : []
      }
      this.dialogVisible = true
      this.$nextTick(function() {
        if (this.$refs.dataForm) this.$refs.dataForm.clearValidate()
      }.bind(this))
    },
    handleViewBoard(row) {
      this.$router.push('/task/boards?project_id=' + row.id)
    },
    command(cmd, row) {
      if (cmd === 'edit') {
        this.handleUpdate(row)
      } else if (cmd === 'delete') {
        this.handleDelete(row)
      }
    },
    submitForm() {
      var self = this
      this.$refs.dataForm.validate(function(valid) {
        if (!valid) return
        self.submitLoading = true
        var data = Object.assign({}, self.form, {
          members: self.form.members.map(function(userId) {
            return { user_id: userId, role: userId === self.form.owner_id ? 'owner' : 'member' }
          })
        })
        var promise = self.form.id ? updateProject(self.form.id, data) : createProject(data)
        promise.then(function(res) {
          if (res.code === 200) {
            self.$message.success(self.form.id ? '更新成功' : '创建成功')
            self.dialogVisible = false
            self.getList()
          } else {
            self.$message.error((res && res.message) || '操作失败')
          }
          self.submitLoading = false
        }).catch(function() {
          self.$message.error('操作失败')
          self.submitLoading = false
        })
      })
    },
    handleDelete(row) {
      this.$confirm('确认删除该项目吗？删除后项目下的看板和任务也将被删除', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(function() {
        return deleteProject(row.id)
      }).then(function(res) {
        if (res.code === 200) {
          this.$message.success('删除成功')
          this.getList()
        } else {
          this.$message.error((res && res.message) || '删除失败')
        }
      }.bind(this)).catch(function() {})
    },
    statusType(status) {
      var map = { 0: '', 1: 'success', 2: 'warning', 3: 'info' }
      return map[status] || ''
    },
    statusText(status) {
      var map = { 0: '进行中', 1: '已完成', 2: '已暂停', 3: '已归档' }
      return map[status] !== undefined ? map[status] : '未知'
    },
    priorityType(priority) {
      var map = { 0: 'info', 1: '', 2: 'warning', 3: 'danger' }
      return map[priority] || ''
    },
    priorityText(priority) {
      var map = { 0: '低', 1: '中', 2: '高', 3: '紧急' }
      return map[priority] !== undefined ? map[priority] : '未知'
    }
  }
}
</script>

<style scoped lang="scss">
.table-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-container {
  margin-bottom: 0;
  padding: 20px;
  background-color: var(--background);
  border-radius: var(--border-radius);
  box-sizing: border-box;
  transition: all 0.3s ease;
}

.mobile-search-top {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 0;
  padding: 10px 15px;
}

.desktop-search-wrapper {
  width: 100%;
}

.search-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.search-input {
  width: 100%;
  max-width: 400px;
  font-size: var(--font-size-sm);
}

.button-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-button,
.add-button {
  white-space: nowrap;
}

/* 筛选面板 */
.filter-panel {
  background-color: var(--bg-100);
  border: 1px solid var(--border);
  border-radius: var(--border-radius);
  padding: 15px;
  margin-top: 10px;
}

.filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;
  label {
    font-size: 13px;
    color: var(--text-secondary);
    white-space: nowrap;
  }
}

.filter-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.applied-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.filter-tag {
  display: inline-flex;
  align-items: center;
  background: var(--primary-light, #ecf5ff);
  color: var(--primary, #409eff);
  border: 1px solid var(--primary-border, #b3d8ff);
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
}

.filter-tag-empty {
  color: var(--text-placeholder, #c0c4cc);
  background: transparent;
  border-color: transparent;
}

.filter-buttons {
  display: flex;
  gap: 8px;
}

/* 内容区域 */
.content-container {
  flex: 1;
  overflow: hidden;
  padding: 0 20px;
}

.table-wrapper {
  height: 100%;
}

.desktop-table {
  font-size: var(--font-size-sm);
}

/* 分页 */
.pagination-container {
  margin-top: 0;
  margin-bottom: 0;
  padding: 10px 20px;
}

/* 项目名称 */
.project-name {
  color: #409EFF;
  font-weight: 500;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
}

.project-desc {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
}

/* 手机端卡片 */
.mobile-list {
  padding: 10px 15px;
}

.mobile-card {
  background: #fff;
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 10px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  cursor: pointer;
}

.mobile-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.mobile-title {
  font-size: 15px;
  font-weight: 500;
  color: #409EFF;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
}

.mobile-card-body {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #606266;
  margin-bottom: 6px;
}

.mobile-meta {
  color: #909399;
}

.mobile-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
}

.mobile-date {
  font-size: 12px;
  color: #909399;
}

.mobile-actions {
  display: flex;
  align-items: center;
}

@media (max-width: 768px) {
  .pagination-container {
    text-align: center;
    padding: 15px;
  }
}
</style>
